import {
  Factory,
  Model,
  belongsTo,
  hasMany,
  trait,
  type Collection,
  type ModelInstance,
} from 'miragejs'
import type { ModelDefinition, AnyRegistry } from 'miragejs/-types'
import type { InvoiceData } from '@/composables/api/Invoice.js'
import {
  randFirstName,
  randLastName,
  randEmail,
  randBoolean,
  randCompanyName,
  randAlphaNumeric,
  randStreetAddress,
  randZipCode,
  randCity,
  randState,
  randSoonDate,
  randPastDate,
  randFutureDate,
  rand,
  randNumber,
  randParagraph,
  randChanceBoolean,
  randMask,
} from '@ngneat/falso'

import {
  createErrorResponse,
  defineRandomTraits,
  includeRequested,
  pageable,
  sortable,
  filterable,
} from './utils.js'
import { toNumber, roundEven } from '@/utils/number.js'
import {
  getCurrentTimestamp,
  getDateTimeTimestamp,
  getTodayDateTimeISO,
} from '@/utils/date/getter.js'
import {
  formatToDateLocaleString,
  formatToDateTimeISO,
} from '@/utils/date/conversion.js'
import type { MockServer, MirageServer } from './server.js'
import { Status } from '@/models/communicationPreferences.js'
import {
  ClientCustomerType,
  ClientStatusOptions,
  ClientTitle,
  type PhoneApiPayload,
  type ClientData,
  type PhoneData,
  type ClientMatchQueryParams,
} from '@/models/client.js'
import { addToDate, subtractToDate } from '@/utils/date/calculation.js'
import type { PatientData } from '@/composables/patients.js'
import type { Server } from 'miragejs/server'

export type ClientDataWithRelations = ClientData & {
  phoneNumbers: Collection<ModelInstance<PhoneData>>
  patients: Collection<ModelInstance<PatientData>>
}

/**
 * Creates invoices for a client with the given count.
 * Shared logic between withInvoices and withFewInvoices traits.
 */
function createInvoicesForClient(
  client: ModelInstance<ClientData>,
  server: Server<AnyRegistry>,
  invoiceCount: number,
) {
  const TODAY = getTodayDateTimeISO()

  const defineInvoiceDates = (index: number) => {
    // Assigns invoice dates: issued date in past, due date also in past or near today.
    // For small fixture counts, ensures at least 2 invoices fall within default 30-day filter.
    if (index === 0 || index === 1) {
      const daysAgo = randNumber({ min: 5, max: 20 })
      const invoiceIssuedDate = subtractToDate(TODAY, { days: daysAgo })
      // Due date is 5-10 days after issued (stays within 30-day window)
      const invoiceDueDate = addToDate(invoiceIssuedDate, {
        days: randNumber({ min: 5, max: 10 }),
      })
      return {
        invoiceDueDate: invoiceDueDate.toISOString(),
        invoiceIssuedDate: invoiceIssuedDate.toISOString(),
      }
    }

    // Define date ranges in chronological order for remaining invoices
    const dateRanges = [
      { start: 2, end: 8, days: { min: 31, max: 60 } }, // Next 6: past 1-2 months
      { start: 8, end: 15, days: { min: 60, max: 90 } }, // Next 7: past 2-3 months
      { start: 15, end: 32, days: { min: 90, max: 150 } }, // Next 17: past 3-5 months
      { start: 32, end: 42, days: { min: 150, max: 200 } }, // Next 10: oldest
      { start: 42, end: 50, days: 0 }, // Last 8: today (covers index 49)
    ]

    const range = dateRanges.find((r) => index >= r.start && index < r.end)
    if (!range) {
      throw new Error(`Unsupported invoice index: ${index}`)
    }
    const days =
      typeof range.days === 'number'
        ? range.days
        : randNumber({
            min: range.days.min || 0,
            max: range.days.max || 0,
          })

    const invoiceIssuedDate = subtractToDate(TODAY, { days })
    const invoiceDueDate = addToDate(invoiceIssuedDate, { days: 20 })

    return {
      invoiceDueDate: invoiceDueDate.toISOString(),
      invoiceIssuedDate: invoiceIssuedDate.toISOString(),
    }
  }

  for (let index = 0; index < invoiceCount; index++) {
    // Create the invoice
    const invoiceTraits = defineRandomTraits({
      withRows: 1.0,
      withFinalization: 1.0,
      withNoPayments: 0.2,
      withPartialPayment: 0.2,
      withFullPayment: 0.6,
    })

    const invoiceDates = defineInvoiceDates(index)
    const invoice = server.create('invoice', ...invoiceTraits, {
      clientId: client.id,
      invoice_date: invoiceDates.invoiceIssuedDate,
      invoice_due_date: invoiceDates.invoiceDueDate,
    })

    const invoiceAttributes = invoice.attrs as InvoiceData & {
      paymentIds: string[]
    }
    // Go through all payments and make sure that the date_added is
    // in the same date or in a future date of the invoice:
    const paymentIds = invoiceAttributes.paymentIds || []
    for (const paymentId of paymentIds) {
      const payment = server.schema.find('invoicePayment', paymentId)
      const invoiceDateRaw = invoiceAttributes.invoice_date
      if (!payment || !invoiceDateRaw) {
        continue
      }

      const invoiceDate = formatToDateTimeISO(invoiceDateRaw)!
      let futureDate = formatToDateTimeISO(
        addToDate(invoiceDate, {
          days: randNumber({ min: 5, max: 15 }),
          months: randNumber({ min: 0, max: 3 }),
        }),
      )!
      if (getDateTimeTimestamp(futureDate) > getCurrentTimestamp()) {
        futureDate = getTodayDateTimeISO()
      }

      const dateAdded = rand([
        { weight: 0.8, value: invoiceDate },
        { weight: 0.2, value: futureDate },
      ])

      payment.update({ date_added: dateAdded.value })
    }
  }
}

export const model = (Model as ModelDefinition<ClientData>).extend({
  invoicingClient: belongsTo('client', { inverse: null }),
  alternativePayer: belongsTo('client', { inverse: null }),
  createdUser: belongsTo('user'),
  modifiedUser: belongsTo('user'),
  homeDepartment: belongsTo('department'),
  phoneNumbers: hasMany('phone', { inverse: 'client' }),
  tagTexts: hasMany('tagText'),
  tags: hasMany('tag', { inverse: null }),
  patients: hasMany('patient', { inverse: 'client' }),
  communicationPreferences: belongsTo('communicationPreference'),
})

/**
 * @todo: add definitions for all the relationships:
 * - old_client_id?: string | null;
 * - merge_hash?: string | null;
 */
export const factory = Factory.extend<ClientData>({
  first_name() {
    return randFirstName()
  },
  last_name() {
    return randLastName()
  },
  full_name() {
    return `${this.first_name} ${this.last_name}`
  },
  email() {
    return randEmail({
      firstName: this.first_name as string,
      lastName: this.last_name as string,
      provider: 'example.org',
    })
  },
  alt_emails() {
    return rand([
      randEmail({
        firstName: this.first_name as string,
        provider: rand(['example.org', 'test.com', 'demo.net']),
      }),
      undefined,
    ])
  },
  organization_name() {
    return randCompanyName()
  },
  vat_number() {
    return randMask('###############').toString()
  },
  register_number() {
    return randMask('#########').toString()
  },
  street_address() {
    return randStreetAddress()
  },
  street_address_2() {
    if (randChanceBoolean({ chanceTrue: 0.3 }) && this.country === 'US') {
      return randStreetAddress()
    }
    return undefined
  },
  street_address_3() {
    if (randChanceBoolean({ chanceTrue: 0.1 }) && this.country === 'US') {
      return randStreetAddress()
    }
    return undefined
  },
  zip_code() {
    return randZipCode()
  },
  city() {
    return randCity()
  },
  state() {
    return rand(['CA', 'NY', 'TX', 'FL', undefined])
  },
  country() {
    return rand(['GB', 'NL', 'FI', 'US'])
  },
  country_old() {
    return rand([
      rand(['GB', 'NL', 'FI', 'US'].filter((el) => el !== this.country)),
      undefined,
    ])
  },
  country_region() {
    return rand([randState(), undefined])
  },
  consent_status() {
    return rand(Object.values(Status))
  },
  consent_valid_to() {
    switch (this.consent_status as Status) {
      case Status.Agreed:
        return formatToDateLocaleString(randFutureDate({ years: 1 }), 'en-US')
      case Status.Declined:
        return formatToDateLocaleString(getTodayDateTimeISO(), 'en-US')
      case Status.Expired:
        return formatToDateLocaleString(randPastDate(), 'en-US')
      case Status.ExpiredSoon:
        return formatToDateLocaleString(randSoonDate({ days: 30 }), 'en-US')
      case Status.NotAsked:
      default:
        return undefined
    }
  },
  status() {
    return rand(Object.values(ClientStatusOptions))
  },
  id_number() {
    return randMask('##########').toString()
  },
  created() {
    return formatToDateLocaleString(randPastDate({ years: 1 }), 'en-US')
  },
  modified() {
    return formatToDateLocaleString(randPastDate(), 'en-US')
  },
  no_email() {
    return randBoolean()
  },
  no_sms() {
    return randBoolean()
  },
  external() {
    return randChanceBoolean({ chanceTrue: 0.8 })
  },
  referring_organization() {
    return randBoolean()
  },
  referring_vet() {
    return randBoolean()
  },
  production_animal_client() {
    return randBoolean()
  },
  farm_code() {
    return randBoolean()
      ? randAlphaNumeric({ length: 8 }).join('').toUpperCase()
      : undefined
  },
  imported() {
    return randBoolean()
  },
  has_alive_patients() {
    return randBoolean()
  },
  alternative_invoicing_address_exist() {
    return randBoolean()
  },
  on_stop() {
    return randBoolean()
  },
  einvoice_address() {
    return randStreetAddress()
  },
  einvoice_operator() {
    return randCompanyName()
  },
  title() {
    return rand(Object.values(ClientTitle))
  },
  permanently_deleted() {
    return randBoolean()
  },
  unsubscribed() {
    return rand([formatToDateLocaleString(randPastDate(), 'en-US'), undefined])
  },
  due_date_delay() {
    return rand([randFutureDate({ years: 1 }).getTime(), undefined])
  },
  date_imported() {
    return randChanceBoolean({ chanceTrue: 0.3 })
      ? formatToDateLocaleString(randPastDate({ years: 1 }), 'en-US')
      : undefined
  },
  customer_type() {
    return rand(Object.values(ClientCustomerType))
  },
  archived() {
    return randChanceBoolean({ chanceTrue: 0.3 })
  },
  remarks() {
    return randChanceBoolean({ chanceTrue: 0.3 }) ? randParagraph() : undefined
  },
  withRandomCriticalNotes: trait({
    afterCreate(client) {
      if (randBoolean()) {
        client.update({
          critical_notes: randParagraph(),
        })
      }
      if (randBoolean()) {
        client.update({
          critical_accounting_notes: randParagraph(),
        })
      }
    },
  }),
  withSecondaryOwner: trait({
    afterCreate(client) {
      client.update({
        secondary_owner_first_name: randFirstName(),
        secondary_owner_last_name: randLastName(),
      })
    },
  }),
  withInvoicingClient: trait({
    afterCreate(client, server) {
      if (!client.invoicingClient) {
        client.update({ invoicingClient: server.create('client') })
      }
    },
  }),
  withInvoices: trait({
    afterCreate(client, server) {
      createInvoicesForClient(client, server as MirageServer, 50)
    },
  }),
  withFewInvoices: trait({
    afterCreate(client, server) {
      createInvoicesForClient(client, server as MirageServer, 3)
    },
  }),
  withPrepayments: trait({
    afterCreate(client, server) {
      const TODAY = getTodayDateTimeISO()

      const calculateDaysFromRange = (
        daysConfig?: number | { min?: number; max?: number },
      ) => {
        // No `daysConfig` - defaults to today
        if (!daysConfig) {
          return 0
        }

        // Fixed number of days specified
        if (typeof daysConfig === 'number') {
          return daysConfig
        }

        // Handle incomplete range object
        if (daysConfig.min === undefined || daysConfig.max === undefined) {
          return daysConfig.min ?? daysConfig.max ?? 0
        }

        // Future dates (negative days) - use random value within range
        if (daysConfig.min < 0) {
          return randNumber({ min: daysConfig.min, max: daysConfig.max })
        }

        // Exact day count specified
        if (daysConfig.min === daysConfig.max) {
          return daysConfig.min
        }

        // Past date range - use min + 1 to avoid collision with other ranges
        return daysConfig.min + 1
      }

      const defineRandomDate = (index: number) => {
        // Define date ranges in chronological order, adjusted for 15 items
        const dateRanges = [
          { start: 0, end: 2, days: { min: 91, max: 200 } }, // 2 items: oldest
          { start: 2, end: 4, days: { min: 61, max: 90 } }, // 2 items: 3 months
          { start: 4, end: 8, days: { min: 31, max: 60 } }, // 4 items: 2 months
          { start: 8, end: 11, days: { min: 2, max: 30 } }, // 3 items: 1 month
          { start: 11, end: 12, days: 1 }, // 1 item: yesterday
          { start: 12, end: 13, days: 0 }, // 1 item: today
          // Add future dates for testing current_balance
          { start: 13, end: 14, days: { min: -5, max: -3 } }, // 1 item: 3-5 days in future
          { start: 14, end: 15, days: { min: -15, max: -7 } }, // 1 item: 7-15 days in future
        ]

        const range = dateRanges.find((r) => index >= r.start && index < r.end)
        const days = calculateDaysFromRange(range?.days)
        const prepaymentDate = subtractToDate(TODAY, { days })
        return prepaymentDate.toISOString()
      }

      // Get department and payment method for the unallocated payments
      const department =
        server.schema.first('department') || server.create('department')
      const paymentMethod =
        server.schema.first('paymentMethod') || server.create('paymentMethod')

      // Create 15 prepayments individually (including 2 with future dates)
      for (let index = 0; index < 15; index++) {
        const prepaymentDate = defineRandomDate(index)

        // Create prepayment (ClientStatementPrepayment) for client statements
        server.create('prepayment', {
          client,
          date_added: prepaymentDate,
        })

        // Create unallocated payment for invoice payment application
        server.create('unallocatedPayment', {
          client,
          department,
          paymentMethod,
          date_added: prepaymentDate,
        })
      }
    },
  }),
  withPrimaryOwnerAsInvoicingClient: trait({
    afterCreate(client) {
      if (!client.invoicingClient) {
        client.update({ invoicingClient: client })
      }
    },
  }),
  withPhoneNumbers: trait({
    afterCreate(client, server) {
      if (!client.phoneNumbers.length) {
        client.update({
          phoneNumbers: server.createList(
            'phone',
            randNumber({ min: 1, max: 2 }),
          ),
        })
      }
    },
  }),
  withMobilePhone: trait({
    afterCreate(client, server) {
      if (!client.phoneNumbers.length) {
        client.update({
          phoneNumbers: [
            server.create('phone', { type_code: 'PERSONAL_PHONE' }),
          ],
        })
      }
    },
  }),
  withTags: trait({
    afterCreate(client, server) {
      const tagTexts = client.tagTexts.length
        ? client.tagTexts
        : server.createList('tagText', randNumber({ min: 3, max: 4 }))

      const tags = tagTexts.map((tagText: ModelInstance) =>
        server.create('tag', { tagText }),
      )

      client.update({ tagTexts, tags })
    },
  }),
  withAlternativePayer: trait({
    afterCreate(client, server) {
      if (!client.alternativePayer) {
        client.update({ alternativePayer: server.create('client') })
      }
    },
  }),
  withPatients: trait({
    afterCreate(client, server) {
      if (!client.patients.length) {
        const patients = server.createList(
          'patient',
          randNumber({ min: 3, max: 10 }),
        )
        patients.forEach((patient) => patient.update({ clientId: client.id }))
        client.update({ patients })
      }
    },
  }),
  withUnarchivedPatients: trait({
    afterCreate(client, server) {
      if (!client.patients.length) {
        const patients = server.createList(
          'patient',
          randNumber({ min: 2, max: 5 }),
          'withUnarchived',
        )
        client.update({ patients })
      }
    },
  }),
  withCommunicationPreferences: trait({
    afterCreate(client, server) {
      client.update({
        communicationPreferences: server.create('communicationPreference'),
      })
    },
  }),
})

export function routes(server: MockServer) {
  server.resource('client')

  server.get('/clients', function (this: MirageServer, schema, request) {
    const clients = schema.all('client')

    // Apply search filter
    const filtered = filterable(clients, request, {
      search: (client, value) => {
        const searchTerm = value.toLowerCase()
        const fullName =
          `${client.attrs.first_name} ${client.attrs.last_name}`.toLowerCase()

        return fullName.includes(searchTerm)
      },
    })

    // first we sort
    const sorted = sortable(filtered, request)

    // then we paginate
    const { data, meta } = pageable(sorted, request)

    // and finally construct the response
    return {
      ...this.serialize(data),
      meta: {
        pagination: meta,
      },
    }
  })

  server.get(
    '/clients/check_duplicate',
    function (this: MirageServer, schema, request) {
      const {
        email,
        id_number,
        street_address,
        zip_code,
        register_number,
      }: ClientMatchQueryParams = request.queryParams

      const filtered = schema.where(
        'client',
        (client: ClientData) =>
          (email && client.email === email) ||
          (id_number && client.id_number === id_number) ||
          (register_number && client.register_number === register_number) ||
          (street_address &&
            zip_code &&
            client.street_address === street_address &&
            client.zip_code === zip_code),
      )

      const { data, meta } = pageable(filtered, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
    { timing: 1500 },
  )

  server.post('/clients', function (schema, request) {
    const { data } = JSON.parse(request.requestBody)
    const client = schema.create('client', {
      ...data.attributes,
      archived: false,
    })

    return client
  })

  server.get('/clients/:id', function (this: MirageServer, schema, request) {
    const client = schema.find('client', request.params.id)
    if (!client) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: 'Client not found' }],
      })
    }

    // When the client has patients and the request includes patient
    // relationships, Mirage's serializer crashes because it tries to
    // resolve `patients` on the Patient model. Build the response
    // manually for patients and phone_numbers; otherwise let Mirage handle it.
    const patients = (client.patients?.models || []) as ModelInstance[]
    const includeParam = request.queryParams.include as
      | undefined
      | string
      | string[]
    const requestsPatients = includeRequested(includeParam, 'patients')
    const requestsPhones = includeRequested(includeParam, 'phone')

    if ((patients.length === 0 || !requestsPatients) && !requestsPhones) {
      return client
    }

    // Serialize client without Mirage's include processing
    const serialized = this.serialize(client) as { data?: unknown }
    const included: unknown[] = []

    if (requestsPatients) {
      included.push(
        ...patients.map((patient) => ({
          type: 'Patient',
          id: patient.id,
          attributes: patient.attrs,
          relationships: {
            client: { data: { type: 'Client', id: client.id } },
          },
        })),
      )
    }

    if (requestsPhones) {
      const phones = (client.phoneNumbers?.models || []) as ModelInstance[]
      for (const phone of phones) {
        included.push({
          type: 'Phone',
          id: phone.id,
          attributes: phone.attrs,
        })
      }
    }

    return { data: serialized.data, included }
  })

  server.patch('/clients/:id', function (schema, request) {
    const client = schema.find('client', request.params.id)
    if (!client) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: 'Client not found' }],
      })
    }

    const { data } = JSON.parse(request.requestBody)
    client.update(data.attributes ?? {})

    const phoneNumbers: PhoneApiPayload[] | undefined =
      data.relationships?.write_only_phone_numbers?.data
    if (phoneNumbers?.length) {
      client.update({
        phoneNumbers: phoneNumbers.map((phone) =>
          server.create('phone', phone),
        ),
      })
    }
    return client
  })

  server.get('/clients/:id/balance', function (schema, request) {
    const clientId = request.params.id
    const client = schema.find('client', clientId)

    if (!client) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Client with id ${clientId} not found` }],
      })
    }

    // Get all invoices for this client to calculate realistic balance
    const allInvoices = schema.all('invoice')
    const clientInvoices = allInvoices.filter(
      (invoice) =>
        invoice.attrs.client_id === clientId || invoice.clientId === clientId,
    )

    // Calculate unpaid invoices totals
    let finalizedUnpaid = 0
    let draftUnpaid = 0

    clientInvoices.models.forEach((invoice) => {
      const dueSum = toNumber(invoice.attrs.due_sum)
      const isFinalized = invoice.attrs.invoice_number !== null

      if (dueSum > 0) {
        if (isFinalized) {
          finalizedUnpaid += dueSum
        } else {
          draftUnpaid += dueSum
        }
      }
    })

    const totalUnpaid = finalizedUnpaid + draftUnpaid

    // Get prepayments for this client
    const allPrepayments = schema.all('prepayment')
    const clientPrepayments = allPrepayments.filter(
      (prepayment) =>
        prepayment.attrs.client_id === clientId ||
        prepayment.clientId === clientId,
    )

    let prepaymentBalance = 0
    clientPrepayments.models.forEach((prepayment) => {
      prepaymentBalance += toNumber(prepayment.attrs.current_balance)
    })

    // Get credit notes (negative invoices) for draft and finalized
    const creditNotes = clientInvoices.filter(
      (invoice) => invoice.attrs.credit_note === true,
    )

    let draftCreditNotes = 0
    let finalizedCreditNotes = 0

    creditNotes.models.forEach((cn) => {
      const total = Math.abs(toNumber(cn.attrs.total_with_vat))
      const isFinalized = cn.attrs.invoice_number !== null

      if (isFinalized) {
        finalizedCreditNotes += total
      } else {
        draftCreditNotes += total
      }
    })

    const availableCreditTotal =
      prepaymentBalance + draftCreditNotes + finalizedCreditNotes
    const clientBalance = availableCreditTotal - totalUnpaid

    return {
      data: {
        type: 'client-balance' as const,
        id: clientId,
        attributes: {
          client_balance: roundEven(clientBalance, 2),
          unpaid_invoices: {
            total: roundEven(totalUnpaid, 2),
            finalized: roundEven(finalizedUnpaid, 2),
            draft: roundEven(draftUnpaid, 2),
          },
          available_credit: {
            total: roundEven(availableCreditTotal, 2),
            prepayments: roundEven(prepaymentBalance, 2),
            draft_credit_notes: roundEven(draftCreditNotes, 2),
            finalized_credit_notes: roundEven(finalizedCreditNotes, 2),
          },
        },
        relationships: {
          client: {
            data: {
              id: clientId,
              type: 'Client' as const,
            },
          },
        },
      },
    }
  })
}

export function seeds(server: MockServer) {
  server.create(
    'client',
    'withPrimaryOwnerAsInvoicingClient',
    'withRandomCriticalNotes',
    'withPhoneNumbers',
    'withTags',
    'withSecondaryOwner',
    'withAlternativePayer',
    'withPatients',
    // @note: id_number is required for testing the check_duplicate endpoint
    { first_name: 'Pavel', last_name: 'Padron', id_number: '1234567890' },
  )

  // @note: register_number is required for testing the check_duplicate endpoint
  server.create('client', 'withPatients', {
    organization_name: 'Test Company',
    register_number: 'REG-123456',
    customer_type: 'CUSTOMER_TYPE_COMPANY',
  })

  server.createList('client', 2, 'withPatients', { email: 'sample@mail.com' })
  server.createList('client', 2, { email: 'sample@mail.com' })

  server.createList(
    'client',
    5,
    'withRandomCriticalNotes',
    'withInvoicingClient',
    'withAlternativePayer',
    'withPatients',
    'withCommunicationPreferences',
  )

  server.createList('client', 5, 'withSecondaryOwner', 'withPatients')
  server.createList('client', 5, 'withSecondaryOwner')

  server.createList('client', 5, 'withTags', 'withPatients')
  server.createList('client', 5, 'withTags')
}
